// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import {
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Check,
  CheckCircle,
  Circle,
  Calendar,
  PauseCircle,
  Activity,
  Loader2,
  Edit2,
  X,
  AlertTriangle,
  History,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample ad groups data structure
const sampleAdGroups = {
  'search-1': [
    {
      id: 'ag1',
      name: 'Brand Keywords',
      status: 'Active',
      spend: 5200,
      impressions: 18000,
      clicks: 1200,
      ctr: 6.67,
      cpc: 4.33,
      conversions: 85,
      conversionRate: 7.08,
      costPerConversion: 61.18,
      revenue: 22100,
      roas: 4.25,
      keywords: [
        { id: 'kw1-1', text: 'glowytics brand', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 4200, clicks: 320, ctr: 7.62, cpc: 4.10, conversions: 22, conversionRate: 6.88, costPerConversion: 59.64, revenue: 5800, roas: 4.42 },
        { id: 'kw1-2', text: 'glowytics analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3800, clicks: 280, ctr: 7.37, cpc: 4.25, conversions: 19, conversionRate: 6.79, costPerConversion: 62.63, revenue: 4900, roas: 4.12 },
        { id: 'kw1-3', text: 'glowytics platform', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.40, conversions: 16, conversionRate: 6.96, costPerConversion: 63.25, revenue: 4200, roas: 4.15 },
        { id: 'kw1-4', text: 'glowytics software', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3300, clicks: 210, ctr: 6.36, cpc: 4.50, conversions: 15, conversionRate: 7.14, costPerConversion: 63.00, revenue: 3800, roas: 4.02 },
        { id: 'kw1-5', text: 'glowytics marketing', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3200, clicks: 160, ctr: 5.00, cpc: 4.60, conversions: 13, conversionRate: 8.13, costPerConversion: 56.62, revenue: 3400, roas: 4.60 }
      ]
    },
    {
      id: 'ag2',
      name: 'Generic Keywords',
      status: 'Active',
      spend: 4800,
      impressions: 16500,
      clicks: 1100,
      ctr: 6.67,
      cpc: 4.36,
      conversions: 65,
      conversionRate: 5.91,
      costPerConversion: 73.85,
      revenue: 18200,
      roas: 3.79,
      keywords: [
        { id: 'kw2-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3800, clicks: 250, ctr: 6.58, cpc: 4.20, conversions: 14, conversionRate: 5.60, costPerConversion: 75.00, revenue: 3900, roas: 3.71 },
        { id: 'kw2-2', text: 'analytics platform', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.30, conversions: 13, conversionRate: 5.65, costPerConversion: 76.15, revenue: 3700, roas: 3.75 },
        { id: 'kw2-3', text: 'marketing dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 4.40, conversions: 13, conversionRate: 5.91, costPerConversion: 74.46, revenue: 3600, roas: 3.72 },
        { id: 'kw2-4', text: 'ads performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.50, conversions: 13, conversionRate: 6.19, costPerConversion: 72.69, revenue: 3500, roas: 3.70 },
        { id: 'kw2-5', text: 'marketing roi tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.60, conversions: 12, conversionRate: 6.32, costPerConversion: 72.83, revenue: 3500, roas: 4.01 }
      ]
    },
    {
      id: 'ag3',
      name: 'Competitor Keywords',
      status: 'Active',
      spend: 2500,
      impressions: 10500,
      clicks: 900,
      ctr: 8.57,
      cpc: 2.78,
      conversions: 30,
      conversionRate: 3.33,
      costPerConversion: 83.33,
      revenue: 12200,
      roas: 4.88,
      keywords: [
        { id: 'kw3-1', text: 'google analytics alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 200, ctr: 8.33, cpc: 2.70, conversions: 7, conversionRate: 3.50, costPerConversion: 77.14, revenue: 2600, roas: 4.85 },
        { id: 'kw3-2', text: 'better than semrush', status: 'Active', matchType: 'Exact', qualityScore: 6, impressions: 2200, clicks: 190, ctr: 8.64, cpc: 2.75, conversions: 6, conversionRate: 3.16, costPerConversion: 87.08, revenue: 2500, roas: 4.79 },
        { id: 'kw3-3', text: 'hubspot vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 2100, clicks: 180, ctr: 8.57, cpc: 2.80, conversions: 6, conversionRate: 3.33, costPerConversion: 84.00, revenue: 2400, roas: 4.76 },
        { id: 'kw3-4', text: 'mixpanel alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2000, clicks: 170, ctr: 8.50, cpc: 2.85, conversions: 6, conversionRate: 3.53, costPerConversion: 80.75, revenue: 2400, roas: 4.96 },
        { id: 'kw3-5', text: 'amplitude vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 1800, clicks: 160, ctr: 8.89, cpc: 2.80, conversions: 5, conversionRate: 3.13, costPerConversion: 89.60, revenue: 2300, roas: 5.11 }
      ]
    }
  ],
  'search-2': [
    {
      id: 'ag4',
      name: 'Product Keywords',
      status: 'Active',
      spend: 3800,
      impressions: 14200,
      clicks: 980,
      ctr: 6.90,
      cpc: 3.88,
      conversions: 52,
      conversionRate: 5.31,
      costPerConversion: 73.08,
      revenue: 15600,
      roas: 4.11,
      keywords: [
        { id: 'kw4-1', text: 'analytics dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 3.80, conversions: 12, conversionRate: 5.45, costPerConversion: 69.67, revenue: 3500, roas: 4.18 },
        { id: 'kw4-2', text: 'marketing analytics tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3000, clicks: 210, ctr: 7.00, cpc: 3.85, conversions: 11, conversionRate: 5.24, costPerConversion: 73.55, revenue: 3300, roas: 4.08 },
        { id: 'kw4-3', text: 'ad performance tracker', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.90, conversions: 10, conversionRate: 5.26, costPerConversion: 74.10, revenue: 3000, roas: 4.05 },
        { id: 'kw4-4', text: 'campaign analytics software', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2700, clicks: 180, ctr: 6.67, cpc: 3.95, conversions: 10, conversionRate: 5.56, costPerConversion: 71.10, revenue: 2900, roas: 4.08 },
        { id: 'kw4-5', text: 'marketing performance dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.90, conversions: 9, conversionRate: 5.00, costPerConversion: 78.00, revenue: 2900, roas: 4.14 }
      ]
    },
    {
      id: 'ag5',
      name: 'Category Keywords',
      status: 'Active',
      spend: 4200,
      impressions: 15800,
      clicks: 1050,
      ctr: 6.65,
      cpc: 4.00,
      conversions: 58,
      conversionRate: 5.52,
      costPerConversion: 72.41,
      revenue: 17400,
      roas: 4.14,
      keywords: [
        { id: 'kw5-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 3.95, conversions: 13, conversionRate: 5.65, costPerConversion: 69.92, revenue: 3800, roas: 4.18 },
        { id: 'kw5-2', text: 'digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 8, impressions: 3300, clicks: 220, ctr: 6.67, cpc: 4.00, conversions: 12, conversionRate: 5.45, costPerConversion: 73.33, revenue: 3600, roas: 4.09 },
        { id: 'kw5-3', text: 'ad campaign management', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.05, conversions: 12, conversionRate: 5.71, costPerConversion: 70.88, revenue: 3500, roas: 4.12 },
        { id: 'kw5-4', text: 'marketing performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3000, clicks: 200, ctr: 6.67, cpc: 4.10, conversions: 11, conversionRate: 5.50, costPerConversion: 74.55, revenue: 3300, roas: 4.02 },
        { id: 'kw5-5', text: 'advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.15, conversions: 10, conversionRate: 5.26, costPerConversion: 78.85, revenue: 3200, roas: 4.06 }
      ]
    }
  ],
  'search-3': [
    {
      id: 'ag6',
      name: 'Location Keywords',
      status: 'Active',
      spend: 2800,
      impressions: 11200,
      clicks: 780,
      ctr: 6.96,
      cpc: 3.59,
      conversions: 42,
      conversionRate: 5.38,
      costPerConversion: 66.67,
      revenue: 12600,
      roas: 4.50,
      keywords: [
        { id: 'kw6-1', text: 'marketing analytics istanbul', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2700, roas: 4.47 },
        { id: 'kw6-2', text: 'ankara digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw6-3', text: 'izmir ad campaign management', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 150, ctr: 6.82, cpc: 3.65, conversions: 8, conversionRate: 5.33, costPerConversion: 68.44, revenue: 2500, roas: 4.56 },
        { id: 'kw6-4', text: 'antalya marketing analytics', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.55, conversions: 8, conversionRate: 5.33, costPerConversion: 66.56, revenue: 2400, roas: 4.52 },
        { id: 'kw6-5', text: 'bursa advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.60, conversions: 8, conversionRate: 5.33, costPerConversion: 67.50, revenue: 2400, roas: 4.44 }
      ]
    }
  ],
  'search-4': [
    {
      id: 'ag7',
      name: 'Seasonal Keywords',
      status: 'Paused',
      spend: 1500,
      impressions: 8200,
      clicks: 420,
      ctr: 5.12,
      cpc: 3.57,
      conversions: 18,
      conversionRate: 4.29,
      costPerConversion: 83.33,
      revenue: 5400,
      roas: 3.60,
      keywords: [
        { id: 'kw7-1', text: 'summer marketing analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1800, clicks: 90, ctr: 5.00, cpc: 3.55, conversions: 4, conversionRate: 4.44, costPerConversion: 79.88, revenue: 1200, roas: 3.75 },
        { id: 'kw7-2', text: 'holiday season analytics', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1700, clicks: 85, ctr: 5.00, cpc: 3.60, conversions: 4, conversionRate: 4.71, costPerConversion: 76.50, revenue: 1100, roas: 3.60 },
        { id: 'kw7-3', text: 'black friday marketing tools', status: 'Paused', matchType: 'Exact', qualityScore: 8, impressions: 1600, clicks: 85, ctr: 5.31, cpc: 3.50, conversions: 4, conversionRate: 4.71, costPerConversion: 74.38, revenue: 1100, roas: 3.69 },
        { id: 'kw7-4', text: 'winter campaign analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1600, clicks: 80, ctr: 5.00, cpc: 3.60, conversions: 3, conversionRate: 3.75, costPerConversion: 96.00, revenue: 1000, roas: 3.47 },
        { id: 'kw7-5', text: 'seasonal marketing performance', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1500, clicks: 80, ctr: 5.33, cpc: 3.55, conversions: 3, conversionRate: 3.75, costPerConversion: 94.67, revenue: 1000, roas: 3.52 }
      ]
    }
  ],
  'search-5': [
    {
      id: 'ag8',
      name: 'Promotion Keywords',
      status: 'Active',
      spend: 3200,
      impressions: 12800,
      clicks: 890,
      ctr: 6.95,
      cpc: 3.60,
      conversions: 48,
      conversionRate: 5.39,
      costPerConversion: 66.67,
      revenue: 14400,
      roas: 4.50,
      keywords: [
        { id: 'kw8-1', text: 'marketing analytics discount', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.55, conversions: 10, conversionRate: 5.26, costPerConversion: 67.45, revenue: 3100, roas: 4.59 },
        { id: 'kw8-2', text: 'analytics software promotion', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.60, conversions: 10, conversionRate: 5.56, costPerConversion: 64.80, revenue: 3000, roas: 4.63 },
        { id: 'kw8-3', text: 'special offer marketing tools', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.65, conversions: 10, conversionRate: 5.71, costPerConversion: 63.88, revenue: 2900, roas: 4.54 },
        { id: 'kw8-4', text: 'marketing dashboard deal', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.55, conversions: 9, conversionRate: 5.14, costPerConversion: 69.06, revenue: 2800, roas: 4.51 },
        { id: 'kw8-5', text: 'analytics platform offer', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 170, ctr: 7.08, cpc: 3.60, conversions: 9, conversionRate: 5.29, costPerConversion: 68.00, revenue: 2600, roas: 4.25 }
      ]
    }
  ],
  'search-6': [
    {
      id: 'ag9',
      name: 'Long-tail Keywords',
      status: 'Active',
      spend: 2900,
      impressions: 11600,
      clicks: 810,
      ctr: 6.98,
      cpc: 3.58,
      conversions: 44,
      conversionRate: 5.43,
      costPerConversion: 65.91,
      revenue: 13200,
      roas: 4.55,
      keywords: [
        { id: 'kw9-1', text: 'best marketing analytics platform for small business', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2800, roas: 4.64 },
        { id: 'kw9-2', text: 'how to track marketing campaign performance effectively', status: 'Active', matchType: 'Phrase', qualityScore: 6, impressions: 2400, clicks: 165, ctr: 6.88, cpc: 3.60, conversions: 9, conversionRate: 5.45, costPerConversion: 66.00, revenue: 2700, roas: 4.55 },
        { id: 'kw9-3', text: 'affordable marketing analytics software for startups', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.55, conversions: 9, conversionRate: 5.63, costPerConversion: 63.11, revenue: 2600, roas: 4.59 },
        { id: 'kw9-4', text: 'marketing roi tracking tool for multiple platforms', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 160, ctr: 7.27, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw9-5', text: 'all in one marketing analytics dashboard with reporting', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2200, clicks: 155, ctr: 7.05, cpc: 3.55, conversions: 8, conversionRate: 5.16, costPerConversion: 68.78, revenue: 2500, roas: 4.54 }
      ]
    }
  ],
  'search-7': [
    {
      id: 'ag10',
      name: 'Dynamic Keywords',
      status: 'Active',
      spend: 3500,
      impressions: 13500,
      clicks: 940,
      ctr: 6.96,
      cpc: 3.72,
      conversions: 51,
      conversionRate: 5.43,
      costPerConversion: 68.63,
      revenue: 15300,
      roas: 4.37,
      keywords: [
        { id: 'kw10-1', text: '{marketing analytics} software', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2900, clicks: 200, ctr: 6.90, cpc: 3.70, conversions: 11, conversionRate: 5.50, costPerConversion: 67.27, revenue: 3300, roas: 4.46 },
        { id: 'kw10-2', text: '{digital marketing} dashboard', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2800, clicks: 195, ctr: 6.96, cpc: 3.75, conversions: 11, conversionRate: 5.64, costPerConversion: 66.48, revenue: 3200, roas: 4.38 },
        { id: 'kw10-3', text: '{campaign performance} tracking', status: 'Active', matchType: 'Dynamic', qualityScore: 6, impressions: 2700, clicks: 185, ctr: 6.85, cpc: 3.70, conversions: 10, conversionRate: 5.41, costPerConversion: 68.45, revenue: 3000, roas: 4.38 },
        { id: 'kw10-4', text: '{advertising analytics} platform', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.75, conversions: 10, conversionRate: 5.56, costPerConversion: 67.50, revenue: 2900, roas: 4.30 },
        { id: 'kw10-5', text: '{marketing roi} calculator', status: 'Active', matchType: 'Dynamic', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.70, conversions: 9, conversionRate: 5.00, costPerConversion: 74.00, revenue: 2900, roas: 4.35 }
      ]
    }
  ]
};

// Sample chart data
const chartData = {
  labels: ['1 Jan', '2 Jan', '3 Jan', '4 Jan', '5 Jan', '6 Jan', '7 Jan'],
  datasets: [
    {
      label: 'Spend',
      data: [300, 450, 320, 500, 350, 200, 400],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    },
    {
      label: 'Revenue',
      data: [500, 300, 400, 600, 450, 350, 550],
      borderColor: 'rgb(16, 185, 129)',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) return `₺${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `₺${(value / 1000).toFixed(0)}K`;
          return `₺${value}`;
        }
      },
      title: {
        display: true,
        text: 'Cost & Revenue (₺)',
        color: '#6b7280',
        font: {
          size: 12,
          weight: '500'
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxTicksLimit: 10
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 15,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += `₺${context.parsed.y.toLocaleString('tr-TR', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
          }
          return label;
        }
      },
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleColor: '#f9fafb',
      bodyColor: '#f9fafb',
      borderColor: 'rgba(75, 85, 99, 0.2)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 12
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

// Impression share biçimlendirme. Google <%10 için 0.0999, >%90 için 0.9001 döner.
const formatImpressionShare = (value) => {
  if (value === null || value === undefined) return null;
  const v = Number(value);
  if (!isFinite(v)) return null;
  if (v > 90.0 && v < 90.05) return '> 90%';
  if (v > 9.98 && v < 10.0) return '< 10%';
  return `${v.toFixed(1)}%`;
};

// Google Ads eşleşme türü etiketleri (Google arayüzündeki İngilizce adlar)
const MATCH_TYPE_LABELS = {
  EXACT: 'Exact',
  PHRASE: 'Phrase',
  BROAD: 'Broad',
  UNKNOWN: '—',
  UNSPECIFIED: '—'
};

export default function SearchCampaigns() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  // Live status/budget overlay (fetched from Google Ads API)
  // Map keyed by String(campaign.id) → { status, budget, budgetId }
  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Inline status/budget editing state
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // campaign.id with open dropdown
  const [editBudget, setEditBudget] = useState(null); // { id: campaign.id, value: string }
  const [updatingId, setUpdatingId] = useState(null); // campaign.id being updated
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm, variant }

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all'); // all | status_change | budget_change

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  const [expandedBgCategories, setExpandedBgCategories] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Spend',
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      }
    ]
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);
  const [topKeywordsData, setTopKeywordsData] = useState([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(true);

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Custom date picker için state'leri ekleyelim
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false); // Sadece burada tanımla

  // Segments için state'ler
  const [selectedSegment, setSelectedSegment] = useState('none');
  const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);
  const [dailyDataMap, setDailyDataMap] = useState(new Map());
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignSearchMode, setCampaignSearchMode] = useState('contains'); // 'contains' | 'not'

  // Reklam grupları: kampanya satırı açıldığında SADECE o kampanya için API'den çekilir
  const [adGroupsByCampaign, setAdGroupsByCampaign] = useState({}); // { [campaignId]: AdGroup[] }
  const [adGroupsLoading, setAdGroupsLoading] = useState(new Set()); // yükleniyor olan kampanya id'leri
  const [adGroupsError, setAdGroupsError] = useState({}); // { [campaignId]: string }

  // Anahtar kelimeler: reklam grubu satırı açıldığında SADECE o grup için anlık çekilir
  const [keywordsByAdGroup, setKeywordsByAdGroup] = useState({}); // { [adGroupId]: Keyword[] }
  const [keywordsLoading, setKeywordsLoading] = useState(new Set()); // yükleniyor olan grup id'leri
  const [keywordsError, setKeywordsError] = useState({}); // { [adGroupId]: string }
  const [impressionShareStatus, setImpressionShareStatus] = useState({}); // { [adGroupId]: { status, error } }

  // Açılan kampanyanın reklam gruplarını çeker (yalnızca o kampanya, tek istek, sonuç cache'lenir)
  const fetchAdGroupsForCampaign = useCallback(async (campaignId) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId || !campaignId) return;

    setAdGroupsLoading(prev => {
      const next = new Set(prev);
      next.add(campaignId);
      return next;
    });
    setAdGroupsError(prev => {
      if (!prev[campaignId]) return prev;
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });

    try {
      const params = new URLSearchParams();
      params.set('campaignId', String(campaignId));
      params.set('dateRange', dateRange);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.set('startDate', formatLocalDate(customStartDate));
        params.set('endDate', formatLocalDate(customEndDate));
      }
      if (gaAccountFilter !== 'all') params.set('accountId', gaAccountFilter);

      const response = await fetch(
        `https://api.glowytics.com/api/google-ads/search-campaign-adgroups?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setAdGroupsByCampaign(prev => ({ ...prev, [campaignId]: data.adGroups || [] }));
    } catch (error) {
      console.error('Error fetching ad groups for campaign', campaignId, error);
      setAdGroupsError(prev => ({ ...prev, [campaignId]: error.message || 'Reklam grupları alınamadı' }));
    } finally {
      setAdGroupsLoading(prev => {
        const next = new Set(prev);
        next.delete(campaignId);
        return next;
      });
    }
  }, [dateRange, customStartDate, customEndDate, gaAccountFilter]);

  // Açılan reklam grubunun anahtar kelimelerini çeker (tek grup, tek istek, sonuç cache'lenir)
  const fetchKeywordsForAdGroup = useCallback(async (campaignId, adGroupId) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId || !adGroupId) return;

    setKeywordsLoading(prev => {
      const next = new Set(prev);
      next.add(adGroupId);
      return next;
    });
    setKeywordsError(prev => {
      if (!prev[adGroupId]) return prev;
      const next = { ...prev };
      delete next[adGroupId];
      return next;
    });

    try {
      const params = new URLSearchParams();
      params.set('adGroupId', String(adGroupId));
      if (campaignId) params.set('campaignId', String(campaignId));
      params.set('dateRange', dateRange);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.set('startDate', formatLocalDate(customStartDate));
        params.set('endDate', formatLocalDate(customEndDate));
      }
      if (gaAccountFilter !== 'all') params.set('accountId', gaAccountFilter);

      const response = await fetch(
        `https://api.glowytics.com/api/google-ads/search-adgroup-keywords?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setKeywordsByAdGroup(prev => ({ ...prev, [adGroupId]: data.keywords || [] }));
      setImpressionShareStatus(prev => ({
        ...prev,
        [adGroupId]: { status: data.impressionShareStatus || 'ok', error: data.impressionShareError || null }
      }));
    } catch (error) {
      console.error('Error fetching keywords for ad group', adGroupId, error);
      setKeywordsError(prev => ({ ...prev, [adGroupId]: error.message || 'Anahtar kelimeler alınamadı' }));
    } finally {
      setKeywordsLoading(prev => {
        const next = new Set(prev);
        next.delete(adGroupId);
        return next;
      });
    }
  }, [dateRange, customStartDate, customEndDate, gaAccountFilter]);

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    // State güncelleyicisi render sırasında çalıştığı için "açılıyor mu" kararını
    // mevcut render'ın state'inden ver.
    const willExpand = !expandedCampaigns.has(campaignId);

    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });

    // Sadece açılırken ve daha önce çekilmemişse istek at
    if (willExpand && !adGroupsByCampaign[campaignId] && !adGroupsLoading.has(campaignId)) {
      fetchAdGroupsForCampaign(campaignId);
    }
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e, campaignId) => {
    if (e) e.stopPropagation();
    const willExpand = !expandedAdGroups.has(adGroupId);

    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });

    // Sadece açılırken ve daha önce çekilmemişse anlık istek at
    if (willExpand && !keywordsByAdGroup[adGroupId] && !keywordsLoading.has(adGroupId)) {
      fetchKeywordsForAdGroup(campaignId, adGroupId);
    }
  };

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alan üzerine tıklandığında sıralama yönünü değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni bir alan seçildiğinde, varsayılan olarak azalan sıralama yap
      setSortField(field);
      setSortDirection(field === 'budget' ? 'desc' : 'asc');
    }
  };

  // Anahtar kelimeleri sırala
  const sortKeywords = (keywords, sortFieldRaw, direction) => {
    if (!sortFieldRaw) return keywords;
    // Kampanya tablosundaki "name" sütunu kelimelerde "text" alanına karşılık gelir
    const field = sortFieldRaw === 'name' ? 'text' : sortFieldRaw;

    return [...keywords].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (anahtar kelime metni, durum vb.)
      if (['text', 'status'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Bilinmeyen alanlarda (ör. GA4/Adjust sütunları) harcamaya göre azalan sırala
      return (b.spend || 0) - (a.spend || 0);
    });
  };

  // Function to organize keyword data into campaigns and ad groups
  const organizeKeywordData = (data) => {
    if (!data || data.length === 0) {
      console.log('No keyword data available');
      return { campaigns: [] };
    }

    console.log(`Organizing ${data.length} keyword records`);
    
    // 🔍 DEBUG: Raw data sample'ını kontrol et
    console.log('📊 Raw data sample:', data.slice(0, 3));
    console.log('📅 Date field samples:', data.slice(0, 5).map(item => ({
      campaign_id: item.campaign_id,
      campaign_name: item.campaign_name,
      date: item.date,
      cost: item.cost
    })));

    // Kampanya ve reklam grupları için map'ler oluştur
    const campaignMap = new Map();
    const adGroupMap = new Map();
    
    // Günlük veriler için map oluştur - campaignId_date key ile
    const dailyDataMapLocal = new Map();
    
    // Anahtar kelimeleri birleştirmek için bir map oluştur
    const keywordMap = new Map();

    // Önce tüm kampanya ve reklam gruplarını oluştur
    data.forEach(item => {
      const campaignId = item.campaign_id;
      const date = item.date;
      
      // 🗓️ Günlük veriler için daily data map'i oluştur
      if (date) {
        const dailyKey = `${campaignId}_${date}`;
        if (!dailyDataMapLocal.has(dailyKey)) {
          dailyDataMapLocal.set(dailyKey, {
            campaignId: campaignId,
            date: date,
            name: item.campaign_name || 'Unknown Campaign',
            status: item.campaign_status || 'Unknown',
            budget: parseFloat(item.budget_amount || 0),
            spend: parseFloat(item.cost || 0),
            impressions: parseInt(item.impressions || 0, 10),
            clicks: parseInt(item.clicks || 0, 10),
            conversions: parseInt(item.conversions || 0, 10),
            conversionValue: parseFloat(item.conversion_value || 0),
            ga4_sessions: parseInt(item.ga4_sessions || 0, 10),
            ga4_purchases: parseInt(item.ga4_purchases || 0, 10),
            ga4_total_revenue: parseFloat(item.ga4_total_revenue || 0),
            adjust_sessions: parseInt(item.adjust_sessions || 0, 10),
            adjust_installs: parseInt(item.adjust_installs || 0, 10),
            adjust_purchase_events: parseInt(item.adjust_purchase_events || 0, 10),
            adjust_all_revenue: parseFloat(item.adjust_all_revenue || 0)
          });
        } else {
          // Aggregate if multiple entries for same campaign-date
          const dailyData = dailyDataMapLocal.get(dailyKey);
          dailyData.spend += parseFloat(item.cost || 0);
          dailyData.impressions += parseInt(item.impressions || 0, 10);
          dailyData.clicks += parseInt(item.clicks || 0, 10);
          dailyData.conversions += parseInt(item.conversions || 0, 10);
          dailyData.conversionValue += parseFloat(item.conversion_value || 0);
          dailyData.ga4_sessions += parseInt(item.ga4_sessions || 0, 10);
          dailyData.ga4_purchases += parseInt(item.ga4_purchases || 0, 10);
          dailyData.ga4_total_revenue += parseFloat(item.ga4_total_revenue || 0);
          dailyData.adjust_sessions += parseInt(item.adjust_sessions || 0, 10);
          dailyData.adjust_installs += parseInt(item.adjust_installs || 0, 10);
          dailyData.adjust_purchase_events += parseInt(item.adjust_purchase_events || 0, 10);
          dailyData.adjust_all_revenue += parseFloat(item.adjust_all_revenue || 0);
        }
      }
      
      // Use the new data structure from search-campaigns endpoint
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: item.campaign_name || 'Unknown Campaign',
          status: item.campaign_status || 'Unknown',
          budget: parseFloat(item.budget_amount || 0),
          spend: parseFloat(item.cost || 0),
          impressions: parseInt(item.impressions || 0, 10),
          clicks: parseInt(item.clicks || 0, 10),
          conversions: parseInt(item.conversions || 0, 10),
          conversionValue: parseFloat(item.conversion_value || 0),
          ga4_sessions: parseInt(item.ga4_sessions || 0, 10),
          ga4_purchases: parseInt(item.ga4_purchases || 0, 10),
          ga4_total_revenue: parseFloat(item.ga4_total_revenue || 0),
          adjust_sessions: parseInt(item.adjust_sessions || 0, 10),
          adjust_installs: parseInt(item.adjust_installs || 0, 10),
          adjust_purchase_events: parseInt(item.adjust_purchase_events || 0, 10),
          adjust_all_revenue: parseFloat(item.adjust_all_revenue || 0),
          adGroups: [], // No adgroup data in this view
          expanded: false
        });
      } else {
        // Aggregate data if multiple entries for the same campaign exist (e.g., daily data)
        const campaign = campaignMap.get(campaignId);
        campaign.spend += parseFloat(item.cost || 0);
        campaign.impressions += parseInt(item.impressions || 0, 10);
        campaign.clicks += parseInt(item.clicks || 0, 10);
        campaign.conversions += parseInt(item.conversions || 0, 10);
        campaign.conversionValue += parseFloat(item.conversion_value || 0);
        campaign.ga4_sessions += parseInt(item.ga4_sessions || 0, 10);
        campaign.ga4_purchases += parseInt(item.ga4_purchases || 0, 10);
        campaign.ga4_total_revenue += parseFloat(item.ga4_total_revenue || 0);
        campaign.adjust_sessions += parseInt(item.adjust_sessions || 0, 10);
        campaign.adjust_installs += parseInt(item.adjust_installs || 0, 10);
        campaign.adjust_purchase_events += parseInt(item.adjust_purchase_events || 0, 10);
        campaign.adjust_all_revenue += parseFloat(item.adjust_all_revenue || 0);
      }
    });

    // Günlük verilerin metriklerini hesapla
    dailyDataMapLocal.forEach(dailyData => {
      dailyData.ctr = dailyData.impressions > 0 ? (dailyData.clicks / dailyData.impressions) * 100 : 0;
      dailyData.cpc = dailyData.clicks > 0 ? dailyData.spend / dailyData.clicks : 0;
      dailyData.conversionRate = dailyData.clicks > 0 ? (dailyData.conversions / dailyData.clicks) * 100 : 0;
      dailyData.costPerConversion = dailyData.conversions > 0 ? dailyData.spend / dailyData.conversions : 0;
      dailyData.roas = dailyData.spend > 0 ? dailyData.conversionValue / dailyData.spend : 0;
      dailyData.ga4_roas = dailyData.spend > 0 ? dailyData.ga4_total_revenue / dailyData.spend : 0;
      dailyData.cps = dailyData.ga4_sessions > 0 ? dailyData.spend / dailyData.ga4_sessions : 0; // Cost per Session
    });

    // Kampanya metriklerini hesapla
    campaignMap.forEach(campaign => {
      // CTR, CPC, dönüşüm oranı vb. hesapla
      campaign.ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0;
      campaign.cpc = campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0;
      campaign.conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0;
      campaign.costPerConversion = campaign.conversions > 0 ? campaign.spend / campaign.conversions : 0;
      campaign.roas = campaign.spend > 0 ? campaign.conversionValue / campaign.spend : 0;
      campaign.cps = campaign.ga4_sessions > 0 ? campaign.spend / campaign.ga4_sessions : 0;
      
      // Kampanya gelirini conversionValue olarak ayarla
      campaign.revenue = campaign.conversionValue;
    });

    // Daily data map'i state'e kaydet
    setDailyDataMap(dailyDataMapLocal);

    // 🔍 DEBUG: Günlük veriler için log ekle
    console.log('📊 Daily Data Map Debug:');
    console.log('Map size:', dailyDataMapLocal.size);
    console.log('Sample daily keys:', Array.from(dailyDataMapLocal.keys()).slice(0, 5));
    console.log('Sample daily data:', Array.from(dailyDataMapLocal.values()).slice(0, 2));

    // Kampanyaları bir diziye dönüştür
    const campaigns = Array.from(campaignMap.values());
    
    console.log(`Organized ${campaigns.length} campaigns with ${dailyDataMapLocal.size} daily records`);
    
    return { campaigns };
  };

  const processedTopKeywords = useMemo(() => {
    if (!topKeywordsData || topKeywordsData.length === 0) {
        return [];
    }

    const keywordSummary = {};

    topKeywordsData.forEach(item => {
        const keywordText = item.keyword_text;
        if (!keywordText || keywordText === '(not set)' || keywordText === '--') return;

        if (!keywordSummary[keywordText]) {
            keywordSummary[keywordText] = {
                keyword: keywordText,
                cost: 0,
                clicks: 0,
                impressions: 0,
                conversions: 0,
                conversionValue: 0,
            };
        }

        const summary = keywordSummary[keywordText];
        summary.cost += parseFloat(item.cost || 0);
        summary.clicks += parseInt(item.clicks || 0, 10);
        summary.impressions += parseInt(item.impressions || 0, 10);
        summary.conversions += parseInt(item.conversions || 0, 10);
        summary.conversionValue += parseFloat(item.conversion_value || 0);
    });
    
    const keywordsArray = Object.values(keywordSummary).map(k => ({
        ...k,
        ctr: k.impressions > 0 ? (k.clicks / k.impressions) * 100 : 0,
        cpc: k.clicks > 0 ? k.cost / k.clicks : 0,
        roas: k.cost > 0 ? k.conversionValue / k.cost : 0,
        conversionRate: k.clicks > 0 ? (k.conversions / k.clicks) * 100 : 0
    }));

    // Sort by cost and take top 20 for scrollable list
    return keywordsArray.sort((a, b) => b.cost - a.cost).slice(0, 20);
  }, [topKeywordsData]);

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        revenue: 0,
        roas: 0,
        ga4Revenue: 0,
        ga4Roas: 0,
        conversions: 0,
        conversionRate: 0,
        ga4Conversions: 0,
        ga4ConversionRate: 0,
        clicks: 0,
        sessions: 0,
        adjustSessions: 0,
        cpc: 0,
        cost: 0,
      };
    }

    let totalCost = 0;
    let totalRevenue = 0;
    let totalGa4Revenue = 0;
    let totalConversions = 0;
    let totalGa4Conversions = 0; // ga4_purchases
    let totalClicks = 0;
    let totalSessions = 0; // ga4_sessions
    let totalAdjustSessions = 0; // adjust_sessions

    campaigns.forEach(campaign => {
      totalCost += campaign.spend || 0;
      totalRevenue += campaign.conversionValue || 0;
      totalGa4Revenue += campaign.ga4_total_revenue || 0;
      totalConversions += campaign.conversions || 0;
      totalGa4Conversions += campaign.ga4_purchases || 0;
      totalClicks += campaign.clicks || 0;
      totalSessions += campaign.ga4_sessions || 0;
      totalAdjustSessions += campaign.adjust_sessions || 0;
    });

    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;
    const ga4Roas = totalCost > 0 ? totalGa4Revenue / totalCost : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) : 0;
    const ga4ConversionRate = totalSessions > 0 ? (totalGa4Conversions / totalSessions) : 0;
    const cpc = totalClicks > 0 ? totalCost / totalClicks : 0;

    return {
      revenue: totalRevenue,
      roas: roas,
      ga4Revenue: totalGa4Revenue,
      ga4Roas: ga4Roas,
      conversions: totalConversions,
      conversionRate: conversionRate,
      ga4Conversions: totalGa4Conversions,
      ga4ConversionRate: ga4ConversionRate,
      clicks: totalClicks,
      sessions: totalSessions,
      adjustSessions: totalAdjustSessions,
      cpc: cpc,
      cost: totalCost,
    };
  };

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        // Fetch both APIs in parallel for better performance
        await Promise.all([
          fetchSearchCampaignData(),
          fetchTopKeywordsData()
        ]);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to fetch campaign data');
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [dateRange, customStartDate, customEndDate, selectedAccountId, gaAccountFilter]);

  // Update campaign status or budget via live Google Ads API
  const updateEntity = useCallback(async (entityType, entityId, field, value, campaignId, oldValue, entityName) => {
    setUpdatingId(campaignId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityType, entityId, field, value, oldValue, entityName }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        toast.success('Updated successfully');
        // Optimistic update in liveDataMap
        setLiveDataMap(prev => {
          const key = String(campaignId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'amount_micros') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 1000000 } };
          }
          return prev;
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId, campaignName) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const micros = Math.round(newValue * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }
    const live = liveDataMap[String(campaignId)];
    if (!live?.budgetId) {
      toast.error('Budget ID not found for this campaign');
      return;
    }
    const oldBudgetMicros = live.budget ? Math.round(live.budget * 1000000) : null;
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity('budget', live.budgetId, 'amount_micros', micros, campaignId, oldBudgetMicros ? String(oldBudgetMicros) : null, campaignName || null);
        setConfirmDialog(null);
      }
    });
  }, [editBudget, liveDataMap, updateEntity]);

  // Fetch change history data
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Fetch live status + daily budget from Google Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });
      const data = await response.json();
      console.log('[SearchCampaigns] campaigns-live response:', { ok: response.ok, success: data.success, count: data.campaigns?.length, error: data.error });
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budgetId: c.budgetId || null
            };
          }
        });
        console.log('[SearchCampaigns] liveDataMap keys:', Object.keys(map));
        setLiveDataMap(map);
      } else {
        console.warn('[SearchCampaigns] campaigns-live failed:', { status: response.status, error: data.error });
      }
    } catch (e) {
      console.error('[SearchCampaigns] campaigns-live fetch error:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Process keyword data when it changes
  useEffect(() => {
    if (campaigns && Array.isArray(campaigns) && campaigns.length > 0) {
      console.log('Processing campaign data...');
      try {
        // This part seems to be for a different data structure.
        // We will rely on the new fetch function.
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing campaign data:', error);
        // Remove the toast error message
        // toast.error('Error processing campaign data');
        setIsLoading(false);
      }
    }
  }, [campaigns]);

  const fetchTopKeywordsData = useCallback(async () => {
    try {
        setIsLoadingKeywords(true);
        const brandId = localStorage.getItem('currentBrandId');
        const targetBrandId = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
        // Dummy keywords for specific brand
        if (brandId === targetBrandId) {
            const baseList = [
                { keyword_text: 'brand analytics', cost: 8200, clicks: 540, impressions: 9200, conversions: 85, conversion_value: 48500 },
                { keyword_text: 'marketing dashboard', cost: 7600, clicks: 500, impressions: 8800, conversions: 72, conversion_value: 41200 },
                { keyword_text: 'campaign performance tracker', cost: 6900, clicks: 470, impressions: 8300, conversions: 66, conversion_value: 38900 },
                { keyword_text: 'ads reporting tool', cost: 6100, clicks: 420, impressions: 7700, conversions: 58, conversion_value: 34200 },
                { keyword_text: 'ppc analytics software', cost: 5800, clicks: 400, impressions: 7400, conversions: 55, conversion_value: 33100 },
                { keyword_text: 'google ads automation', cost: 5400, clicks: 380, impressions: 7000, conversions: 50, conversion_value: 30800 },
                { keyword_text: 'search ads optimization', cost: 5200, clicks: 360, impressions: 6800, conversions: 48, conversion_value: 29700 }
            ];
            setTopKeywordsData(baseList);
            return;
        }
        const kwParams = new URLSearchParams();
        kwParams.set('dateRange', dateRange);
        if (dateRange === 'custom' && customStartDate && customEndDate) {
          kwParams.set('startDate', new Date(customStartDate).toISOString().split('T')[0]);
          kwParams.set('endDate', new Date(customEndDate).toISOString().split('T')[0]);
        }
        if (gaAccountFilter !== 'all') kwParams.set('accountId', gaAccountFilter);
        const response = await fetch(`https://api.glowytics.com/api/google-ads/keyword-full-performance?${kwParams.toString()}`, {
            credentials: 'include',
            headers: { 'X-Brand-ID': brandId }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch top keywords data');
        }
        const result = await response.json();
        startTransition(() => {
        setTopKeywordsData(result);
        });
    } catch (error) {
        console.error('Error fetching top keywords data:', error);
        toast.error('Failed to load top keywords data');
        setTopKeywordsData([]);
    } finally {
        setIsLoadingKeywords(false);
    }
  }, [dateRange, customStartDate, customEndDate, gaAccountFilter]);

  // Tarih aralığı / hesap filtresi değişince reklam grubu cache'ini temizle
  useEffect(() => {
    setAdGroupsByCampaign({});
    setAdGroupsError({});
  }, [dateRange, customStartDate, customEndDate, gaAccountFilter]);

  // Local date formatter to avoid TZ shift (YYYY-MM-DD)
  const formatLocalDate = (d) => {
    if (!d) return null;
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Function to fetch keyword data from the database
  const fetchSearchCampaignData = async () => {
    try {
      setIsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const targetBrandId = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoading(false);
        return;
      }

      console.log('Fetching search campaign data for brand ID:', brandId);
      // Synthetic dataset for specific brand; includes Adjust metrics
      if (brandId === targetBrandId) {
        // Build date list
        const now = new Date();
        let start = new Date(now);
        let end = new Date(now);
        if (dateRange === 'last7') {
          start.setDate(now.getDate() - 6);
        } else if (dateRange === 'last30') {
          start.setDate(now.getDate() - 29);
        } else if (dateRange === 'yesterday') {
          start.setDate(now.getDate() - 1);
          end.setDate(now.getDate() - 1);
        } else if (dateRange === 'custom' && customStartDate && customEndDate) {
          start = new Date(customStartDate);
          end = new Date(customEndDate);
        }
        const dayList = [];
        const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const lastDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        while (cursor <= lastDate) {
          dayList.push(cursor.toISOString().slice(0, 10));
          cursor.setDate(cursor.getDate() + 1);
        }
        const campaignsMeta = [
          { id: 'GGL-SRC-001', name: 'Search | Brand', status: 'enabled' },
          { id: 'GGL-SRC-002', name: 'Search | Generic', status: 'enabled' },
          { id: 'GGL-SRC-003', name: 'Search | Competitor', status: 'enabled' }
        ];
        const rawData = [];
        dayList.forEach((day, di) => {
          campaignsMeta.forEach((c, ci) => {
            const factor = 1 + (di / Math.max(1, dayList.length - 1));
            const cost = Math.round((ci + 1) * 9500 * factor); // TRY
            const impressions = Math.round((ci + 2) * 18000 * factor);
            const clicks = Math.round(impressions * (0.055 + ci * 0.004));
            const conversions = Math.round(clicks * (0.075 - ci * 0.01)); // brand > generic > comp
            const convValue = Math.round(conversions * (ci === 0 ? 520 : ci === 1 ? 480 : 450));
            const ga4Sessions = Math.round(clicks * 1.15);
            const ga4Purchases = Math.round(conversions * 0.95);
            const ga4Revenue = Math.round(convValue * 1.02);
            const adjustSessions = Math.round(ga4Sessions * 0.7);
            const adjustInstalls = Math.round(clicks * (0.12 + ci * 0.02));
            const adjustPurchases = Math.round(ga4Purchases * (0.8 - ci * 0.1));
            const adjustRevenue = Math.round(ga4Revenue * (0.85 - ci * 0.05));
            rawData.push({
              campaign_id: c.id,
              campaign_name: c.name,
              campaign_status: c.status,
              budget_amount: Math.round(cost * 3),
              cost,
              impressions,
              clicks,
              conversions,
              conversion_value: convValue,
              date: day,
              ga4_sessions: ga4Sessions,
              ga4_purchases: ga4Purchases,
              ga4_total_revenue: ga4Revenue,
              adjust_sessions: adjustSessions,
              adjust_installs: adjustInstalls,
              adjust_purchase_events: adjustPurchases,
              adjust_all_revenue: adjustRevenue
            });
          });
        });
        const { campaigns: processedCampaigns } = organizeKeywordData(rawData);
        // Use startTransition for non-blocking UI updates
        startTransition(() => {
        setCampaigns(processedCampaigns);
        });
        groupKeywordDataByDate(rawData);
        setIsLoading(false);
        return;
      }
      const scParams = new URLSearchParams();
      scParams.set('dateRange', dateRange);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        scParams.set('startDate', formatLocalDate(customStartDate));
        scParams.set('endDate', formatLocalDate(customEndDate));
      }
      if (gaAccountFilter !== 'all') scParams.set('accountId', gaAccountFilter);

      const response = await fetch(`https://api.glowytics.com/api/google-ads/search-campaigns?${scParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to fetch search campaign data: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('Search campaign data fetched successfully:', data);
      
      if (data && data.rawData) {
        const { campaigns: processedCampaigns } = organizeKeywordData(data.rawData);
        // Use startTransition for non-blocking UI updates
        startTransition(() => {
        setCampaigns(processedCampaigns);
        });
        groupKeywordDataByDate(data.rawData);
      } else {
        setCampaigns([]);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching search campaign data:', error);
      toast.error(`Failed to fetch search campaign data: ${error.message}`);
      setIsLoading(false);
    }
  };
  
  // Update the calculateDateRange function to handle the "This Month" filter
  const calculateDateRange = (selectedRange) => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case '7days':
      case 'last7':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case '30days':
      case 'last30':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
          break;
        }
      case 'all':
      case 'all_time':
        return { startDate: null, endDate: null };
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
    }

    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  };
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, dateRange) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to end of day for end date for inclusive comparison
    endDate.setHours(23, 59, 59, 999);
    
    return data.filter(item => {
      if (!item.date) return false;
      
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };
  
  // New function to group keyword data by date and calculate total cost
  const groupKeywordDataByDate = (keywordData) => {
    if (!keywordData || keywordData.length === 0) {
      updateChart([]); // Grafiği temizle
      return [];
    }
    
    const dailyDataMap = new Map();

    keywordData.forEach(item => {
      if (item.date) {
        const date = item.date;
        if (!dailyDataMap.has(date)) {
          dailyDataMap.set(date, { date, cost: 0, revenue: 0 });
        }
        const dayData = dailyDataMap.get(date);
        dayData.cost += parseFloat(item.cost || 0);
        dayData.revenue += parseFloat(item.conversion_value || 0);
      }
    });

    const dailyDataArray = Array.from(dailyDataMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Update the chart with daily cost and revenue data
    updateChart(dailyDataArray);
    
    return dailyDataArray;
  };
  
  // New function to update the chart with daily cost data
  const updateChart = (dailyData) => {
    if (!dailyData || dailyData.length === 0) {
      console.log("No daily data available to update chart");
      setChartData({ labels: [], datasets: [] }); // Clear chart
      return;
    }
    
    // Format dates for display
    const labels = dailyData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
    
    // Extract cost and revenue values
    const costs = dailyData.map(item => item.cost);
    const revenues = dailyData.map(item => item.revenue);
    
    // Create a new chart data object
    const newChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Cost',
          data: costs,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          label: 'Revenue',
          data: revenues,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        }
      ]
    };
    
    // Set the chart data
    setChartData(newChartData);
    
    console.log("Performance trend chart updated with daily cost and revenue data:", newChartData);
  };
  
  // Performans trend verilerini çekmek için yeni fonksiyon
  const fetchPerformanceTrend = async () => {
    try {
      setIsLoadingTrend(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoadingTrend(false);
        return;
      }
      
      // Use fetch API instead of axios
      const trendParams = new URLSearchParams();
      if (gaAccountFilter !== 'all') trendParams.set('accountId', gaAccountFilter);
      const trendUrl = `https://api.glowytics.com/api/google-ads/performance-trend${trendParams.toString() ? '?' + trendParams.toString() : ''}`;
      const response = await fetch(trendUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Don't try to parse JSON if response is not OK
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch performance trend: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("API Performance Trend Response:", data);

      if (data && data.trend) {
        setTrendData(data.trend);
      } else {
        setTrendData(generateEmptyTrendData());
      }
      setIsLoadingTrend(false);
    } catch (error) {
      console.error("Error fetching performance trend:", error);
      setTrendData(generateEmptyTrendData());
      setIsLoadingTrend(false);
      // Don't show error toast
    }
  };

  // Keyword verilerinden trend verilerini oluşturan yardımcı fonksiyon
  const generateTrendDataFromKeywords = () => {
    try {
      if (!topKeywordsData || !Array.isArray(topKeywordsData) || topKeywordsData.length === 0) {
        console.log("No keyword data available for trend generation");
        return;
      }

      // Create a map to store metrics by date
      const metricsByDate = new Map();

      // Process all keywords directly from topKeywordsData
      topKeywordsData.forEach(keyword => {
        if (keyword.date) {
          const date = keyword.date;
          
          if (!metricsByDate.has(date)) {
            metricsByDate.set(date, {
              date,
              impressions: 0,
              clicks: 0,
              cost: 0,
              conversions: 0,
              conversionValue: 0
            });
          }

          const metrics = metricsByDate.get(date);
          metrics.impressions += parseInt(keyword.impressions || 0, 10);
          metrics.clicks += parseInt(keyword.clicks || 0, 10);
          metrics.cost += parseFloat(keyword.cost || 0);
          metrics.conversions += parseInt(keyword.conversions || 0, 10);
          metrics.conversionValue += parseFloat(keyword.conversion_value || 0);
        }
      });

      // Convert to array and sort by date
      const trendData = Array.from(metricsByDate.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate derived metrics
      trendData.forEach(day => {
        day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
        day.cpc = day.clicks > 0 ? day.cost / day.clicks : 0;
        day.convRate = day.clicks > 0 ? (day.conversions / day.clicks) * 100 : 0;
        day.cpa = day.conversions > 0 ? day.cost / day.conversions : 0;
        day.roas = day.cost > 0 ? day.conversionValue / day.cost : 0;
      });

      console.log("Generated trend data from keywords:", trendData);
      
      return trendData;
    } catch (error) {
      console.error("Error generating trend data:", error);
      return [];
    }
  };
  
  // Boş trend verisi oluşturan yardımcı fonksiyon
  const generateEmptyTrendData = () => {
    console.log("Generating empty trend data for the last 30 days");
    
    // Son 30 günün tarihlerini oluştur
    const labels = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      // Tarihi gün-ay formatında göster
      labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    }
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Spend',
          data: Array(30).fill(0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: Array(30).fill(0),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Normalize campaign status to 'ENABLED' or 'PAUSED'
  const cleanStatus = (s) => {
    if (!s) return '';
    const cleaned = s.replace('CampaignStatus.', '').trim().toUpperCase();
    if (cleaned === 'ACTIVE' || cleaned === 'ENABLED') return 'ENABLED';
    if (cleaned === 'PAUSED') return 'PAUSED';
    return cleaned;
  };

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null) return '0';
    
    const {
      prefix = '',
      suffix = '',
      minimumFractionDigits = 2,
      maximumFractionDigits = 2,
      notation = 'standard'
    } = options;
    
    // Remove any existing currency symbol from the value if it's a string
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/[₺$€£¥]/g, '');
    }
    
    // Convert to number
    numericValue = parseFloat(numericValue);
    
    // Format the number
    return `${prefix}${numericValue.toLocaleString(undefined, {
      minimumFractionDigits,
      maximumFractionDigits,
      notation
    })}${suffix}`;
  };

  // Get summary metrics
  const summaryMetrics = calculateSummaryMetrics(campaigns);
  const cps = summaryMetrics.sessions > 0 ? (summaryMetrics.cost || 0) / summaryMetrics.sessions : 0;

  // Set default sort on mount only (removed duplicate useEffect that was calling undefined fetchKeywordData)
  useEffect(() => {
        setSortField('budget');
        setSortDirection('desc');
  }, []);
  // NOTE: Custom date re-fetch is already handled by the main useEffect above (line 788)
  // which has [dateRange, customStartDate, customEndDate] dependencies

  // Kampanyaları sırala
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    // Synthetic combined revenue (GA4 + Adjust)
    if (sortField === 'total_revenue') {
      const aValue = (parseFloat(a.ga4_total_revenue) || 0) + (parseFloat(a.adjust_all_revenue) || 0);
      const bValue = (parseFloat(b.ga4_total_revenue) || 0) + (parseFloat(b.adjust_all_revenue) || 0);
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    // Sayısal değerler için sıralama
    if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'conversionValue', 'roas', 'ga4_sessions', 'ga4_purchases', 'ga4_total_revenue', 'cps', 'adjust_sessions', 'adjust_installs', 'adjust_purchase_events', 'adjust_all_revenue'].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(a[sortField]) || 0;
      const bValue = parseFloat(b[sortField]) || 0;

      return sortDirection === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'text'].includes(sortField)) {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak budget'a göre azalan sıralama
    return b.budget - a.budget;
  });

  const filteredCampaigns = useMemo(() => {
    const query = campaignSearch.trim().toLowerCase();
    if (!query) return sortedCampaigns;
    if (campaignSearchMode === 'not') {
      return sortedCampaigns.filter(campaign => !((campaign.name || '').toLowerCase().includes(query)));
    }
    return sortedCampaigns.filter(campaign => (campaign.name || '').toLowerCase().includes(query));
  }, [sortedCampaigns, campaignSearch, campaignSearchMode]);

  // Detect presence of Adjust data/connection (heuristic: any non-zero Adjust metric)
  const hasAdjust = useMemo(() => {
    return filteredCampaigns.some(c => ((c.adjust_sessions || 0) + (c.adjust_installs || 0) + (c.adjust_purchase_events || 0) + (c.adjust_all_revenue || 0)) > 0);
  }, [filteredCampaigns]);

  // Brand/Generic classification and aggregation
  const brandGenericSummary = useMemo(() => {
    const rows = {
      Brand: { cost: 0, sessions: 0, clicks: 0, conversions: 0, adsRevenue: 0, ga4Revenue: 0, campaigns: [] },
      Generic: { cost: 0, sessions: 0, clicks: 0, conversions: 0, adsRevenue: 0, ga4Revenue: 0, campaigns: [] },
      'Brand Generic': { cost: 0, sessions: 0, clicks: 0, conversions: 0, adsRevenue: 0, ga4Revenue: 0, campaigns: [] },
    };
    const classify = (name) => {
      const n = (name || '').toLowerCase();
      // Brand Generic — only if the literal phrase "brand generic" is in the name
      if (/brand[\s_-]?generic/.test(n)) return 'Brand Generic';
      // Brand — BRND code or "branding" keyword
      if (n.includes('brnd') || n.includes('branding')) return 'Brand';
      // Everything else (incl. GNRC, plain "generic", competitor, etc.) is Generic
      return 'Generic';
    };

    filteredCampaigns.forEach(c => {
      const key = classify(c.name);
      const r = rows[key];
      const cost = Number(c.spend || 0);
      const sessions = Number(c.ga4_sessions || 0);
      const clicks = Number(c.clicks || 0);
      const conversions = Number(c.conversions || 0);
      const adsRevenue = Number(c.conversionValue || 0);
      const ga4Revenue = Number(c.ga4_total_revenue || 0);
      r.cost += cost;
      r.sessions += sessions;
      r.clicks += clicks;
      r.conversions += conversions;
      r.adsRevenue += adsRevenue;
      r.ga4Revenue += ga4Revenue;
      r.campaigns.push({
        id: c.id || c.campaign_id || c.name,
        name: c.name || '(unnamed)',
        cost, sessions, clicks, conversions, adsRevenue, ga4Revenue,
        cpc: clicks > 0 ? cost / clicks : 0,
        cps: sessions > 0 ? cost / sessions : 0,
      });
    });

    const withRates = Object.entries(rows).map(([category, v]) => {
      const cpc = v.clicks > 0 ? v.cost / v.clicks : 0;
      const cps = v.sessions > 0 ? v.cost / v.sessions : 0;
      // Sort campaigns by cost desc within each category
      const campaigns = [...v.campaigns].sort((a, b) => b.cost - a.cost);
      return { category, ...v, campaigns, cpc, cps };
    });

    const totals = withRates.reduce((acc, r) => {
      acc.cost += r.cost; acc.sessions += r.sessions; acc.clicks += r.clicks; acc.conversions += r.conversions; acc.adsRevenue += r.adsRevenue; acc.ga4Revenue += r.ga4Revenue;
      return acc;
    }, { cost: 0, sessions: 0, clicks: 0, conversions: 0, adsRevenue: 0, ga4Revenue: 0 });
    const totalRow = {
      category: 'Total',
      ...totals,
      cpc: totals.clicks > 0 ? totals.cost / totals.clicks : 0,
      cps: totals.sessions > 0 ? totals.cost / totals.sessions : 0,
    };

    return { rows: withRates, totalRow };
  }, [filteredCampaigns]);

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status', 'text'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Bilinmeyen alanlarda (ör. GA4/Adjust sütunları) harcamaya göre azalan sırala
      return (b.spend || 0) - (a.spend || 0);
    });
  };

  // Get daily data for a campaign
  const getDailyDataForCampaign = (campaignId) => {
    const dailyRecords = [];
    
    // Son X günün verilerini al (segment seçimine göre)
    const daysToShow = selectedSegment === '7days' ? 7 : 
                      selectedSegment === '14days' ? 14 : 
                      selectedSegment === '30days' ? 30 : 0;
    
    if (daysToShow === 0) return [];
    
    console.log(`🔍 Getting daily data for campaign: ${campaignId}, days: ${daysToShow}`);
    console.log('📊 Available daily data map size:', dailyDataMap.size);
    
    // 🔄 YENİ YAKLAŞIM: Seçili date range'e göre tarih aralığı hesapla
    let startDate, endDate;
    
    // Date range'e göre başlangıç ve bitiş tarihlerini hesapla
    switch(dateRange) {
      case 'yesterday':
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday;
        break;
      case 'last7':
        const last7End = new Date();
        const last7Start = new Date();
        last7Start.setDate(last7End.getDate() - 7);
        startDate = last7Start;
        endDate = last7End;
        break;
      case 'last30':
        const last30End = new Date();
        const last30Start = new Date();
        last30Start.setDate(last30End.getDate() - 30);
        startDate = last30Start;
        endDate = last30End;
        break;
      case 'thisMonth':
        const today = new Date();
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else {
          // Fallback
          const fallbackEnd = new Date();
          const fallbackStart = new Date();
          fallbackStart.setDate(fallbackEnd.getDate() - daysToShow);
          startDate = fallbackStart;
          endDate = fallbackEnd;
        }
        break;
      default:
        // Default: Son X gün
        const defaultEnd = new Date();
        const defaultStart = new Date();
        defaultStart.setDate(defaultEnd.getDate() - daysToShow);
        startDate = defaultStart;
        endDate = defaultEnd;
    }
    
    console.log(`📅 Date range: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
    
    // Sadece segment için istenen gün sayısını al (son X gün)
    const segmentEndDate = new Date(endDate);
    const segmentStartDate = new Date(segmentEndDate);
    segmentStartDate.setDate(segmentEndDate.getDate() - daysToShow + 1);
    
    console.log(`🔢 Segment range: ${segmentStartDate.toISOString().split('T')[0]} to ${segmentEndDate.toISOString().split('T')[0]}`);
    
    // Segment tarih aralığındaki günleri oluştur
    const currentDate = new Date(segmentStartDate);
    while (currentDate <= segmentEndDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dailyKey = `${campaignId}_${dateStr}`;
      
      console.log(`🔍 Looking for key: "${dailyKey}"`);
      
      if (dailyDataMap.has(dailyKey)) {
        // Veri var
        const data = dailyDataMap.get(dailyKey);
        console.log(`✅ Found data for ${dateStr}:`, data);
        dailyRecords.push(data);
      } else {
        // Veri yok - boş kayıt oluştur
        console.log(`❌ No data found for ${dateStr}`);
        
        // 🔍 DEBUG: Mevcut key'leri kontrol et (sadece ilk missing date için)
        if (dailyRecords.length === 0) {
          const availableKeys = Array.from(dailyDataMap.keys()).filter(key => key.startsWith(campaignId));
          console.log(`Available keys for campaign ${campaignId}:`, availableKeys.slice(0, 10));
          
          // Date format kontrolü
          const sampleKeys = Array.from(dailyDataMap.keys()).slice(0, 5);
          console.log('Sample keys from map:', sampleKeys);
        }
        
        dailyRecords.push({
          campaignId: campaignId,
          date: dateStr,
          name: '',
          status: '',
          budget: 0,
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          ga4_sessions: 0,
          ga4_purchases: 0,
          ga4_total_revenue: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerConversion: 0,
          roas: 0,
          ga4_roas: 0,
          isEmpty: true // Boş kayıt olduğunu belirt
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log(`📈 Final daily records for ${campaignId}:`, dailyRecords.length, 'records');
    
    // 🚨 FALLBACK: Eğer hiç veri bulunamadıysa, o kampanyaya ait tüm günlük verileri al
    const hasAnyData = dailyRecords.some(record => !record.isEmpty);
    if (!hasAnyData) {
      console.log('🔄 No data found in date range, trying fallback approach...');
      
      // Bu kampanyaya ait tüm günlük verileri bul
      const allCampaignData = Array.from(dailyDataMap.values()).filter(data => 
        data.campaignId === campaignId
      );
      
      console.log(`📊 Found ${allCampaignData.length} total daily records for campaign ${campaignId}`);
      
      if (allCampaignData.length > 0) {
        // Son X günlük veriyi al ve döndür
        const sortedData = allCampaignData
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, daysToShow);
        
        console.log('📈 Using fallback data:', sortedData);
        return sortedData;
      }
    }
    
    return dailyRecords.sort((a, b) => new Date(b.date) - new Date(a.date)); // En son tarih üstte
  };

  // Aggregate dailyDataMap into monthly buckets for a single campaign.
  // Date field is 'YYYY-MM' so the row renderer can detect monthly vs. daily.
  const getMonthlyDataForCampaign = (campaignId) => {
    const monthsMap = new Map();
    const allCampaignData = Array.from(dailyDataMap.values()).filter(d => d.campaignId === campaignId);
    if (allCampaignData.length === 0) return [];

    for (const d of allCampaignData) {
      const monthKey = (d.date || '').slice(0, 7);
      if (!monthKey) continue;
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          campaignId,
          date: monthKey,
          name: '',
          status: '',
          budget: 0,
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          ga4_sessions: 0,
          ga4_purchases: 0,
          ga4_total_revenue: 0,
          adjust_sessions: 0,
          adjust_installs: 0,
          adjust_purchase_events: 0,
          adjust_all_revenue: 0,
        });
      }
      const m = monthsMap.get(monthKey);
      m.spend += d.spend || 0;
      m.impressions += d.impressions || 0;
      m.clicks += d.clicks || 0;
      m.conversions += d.conversions || 0;
      m.conversionValue += d.conversionValue || 0;
      m.ga4_sessions += d.ga4_sessions || 0;
      m.ga4_purchases += d.ga4_purchases || 0;
      m.ga4_total_revenue += d.ga4_total_revenue || 0;
      m.adjust_sessions += d.adjust_sessions || 0;
      m.adjust_installs += d.adjust_installs || 0;
      m.adjust_purchase_events += d.adjust_purchase_events || 0;
      m.adjust_all_revenue += d.adjust_all_revenue || 0;
    }

    for (const m of monthsMap.values()) {
      m.ctr = m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0;
      m.cpc = m.clicks > 0 ? m.spend / m.clicks : 0;
      m.conversionRate = m.clicks > 0 ? (m.conversions / m.clicks) * 100 : 0;
      m.costPerConversion = m.conversions > 0 ? m.spend / m.conversions : 0;
      m.roas = m.spend > 0 ? m.conversionValue / m.spend : 0;
      m.ga4_roas = m.spend > 0 ? m.ga4_total_revenue / m.spend : 0;
      m.cps = m.ga4_sessions > 0 ? m.spend / m.ga4_sessions : 0;
    }

    return Array.from(monthsMap.values()).sort((a, b) => b.date.localeCompare(a.date));
  };

  // Date dropdown için click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
      if (isSegmentDropdownOpen) {
        setIsSegmentDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDateDropdownOpen, isSegmentDropdownOpen]);

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70 w-full max-w-[100vw] overflow-x-hidden box-border">
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' :
              'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                  confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                  confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                  'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white px-5 py-3 rounded-2xl shadow-sm w-full gap-3 md:gap-0 min-w-0">
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          <img
            src="/search.svg"
            alt="Search Campaigns"
            className="w-7 h-7 object-contain"
          />
          <h1 className="text-lg font-semibold text-gray-900">Search Campaigns</h1>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-end w-full md:w-auto">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı */}
          <div className="text-xs text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'lastMonth':
                  const todayForLastMonth = new Date();
                  const firstDayOfLastMonth = new Date(todayForLastMonth.getFullYear(), todayForLastMonth.getMonth() - 1, 1);
                  const lastDayOfLastMonth = new Date(todayForLastMonth.getFullYear(), todayForLastMonth.getMonth(), 0);
                  return `${firstDayOfLastMonth.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${lastDayOfLastMonth.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces the legacy inline dropdown +
              custom-date modal below). State ownership is unchanged. */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />

        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        {/* Chart */}
        <Card className="col-span-1 lg:col-span-8 rounded-2xl overflow-hidden bg-white min-w-0">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Daily Cost & Revenue Trend</h2>
                <p className="text-sm text-gray-500 mt-1">Campaign daily cost and revenue distribution</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {dataLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics - kartların sırasını değiştirdik */}
        <div className="col-span-1 lg:col-span-4 grid grid-cols-1 gap-3 min-w-0">
          {/* Row 1: Revenue, GA4 Revenue */}
          <div className="grid grid-cols-2 gap-3 min-w-0">
            {/* Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Revenue</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.roas || 0)}x ROAS</p>
              </div>
            </div>
            {/* GA4 Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <div className="text-gray-500 text-xs flex items-center gap-1">
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
                  <span>Revenue</span>
                </div>
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.ga4Revenue) && summaryMetrics.ga4Revenue ? Math.floor(summaryMetrics.ga4Revenue).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.ga4Revenue) && summaryMetrics.ga4Revenue ? Math.floor(summaryMetrics.ga4Revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.ga4Roas || 0)}x ROAS</p>
              </div>
            </div>
          </div>

          {/* Row 2: Cost, Conversions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cost Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Cost</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Conversions Card (Combined) */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Conversions</p>
                <div className="flex items-center gap-1">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-3 h-3" />
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                </div>
              </div>
              <div className="flex items-center justify-around mt-2">
                {/* Ads Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.conversions || 0}</p>
                  <p className="text-[10px] text-gray-500">%{formatNumber(summaryMetrics.conversionRate ? summaryMetrics.conversionRate * 100 : 0, {maximumFractionDigits: 2})} CR</p>
                </div>
                {/* Separator */}
                <div className="h-10 border-l border-gray-200"></div>
                {/* GA4 Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.ga4Conversions || 0}</p>
                  <p className="text-[10px] text-gray-500">%{formatNumber(summaryMetrics.ga4ConversionRate ? summaryMetrics.ga4ConversionRate * 100 : 0, {maximumFractionDigits: 2})} CR</p>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Clicks, Sessions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Clicks Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Clicks</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Sessions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Sessions</p>
                {hasAdjust ? (
                  <div className="flex items-center gap-1">
                    <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3" />
                    <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                  </div>
                ) : (
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
                )}
              </div>
              {hasAdjust ? (
                <div className="flex items-center justify-around mt-2">
                  {/* Adjust Sessions */}
                  <div className="text-center w-full min-w-0">
                    <p className="text-xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.adjustSessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.adjustSessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                    <p className="text-[10px] text-gray-500">Adj. Sessions</p>
                  </div>
                  {/* Separator */}
                  <div className="h-10 border-l border-gray-200"></div>
                  {/* GA4 Sessions */}
                  <div className="text-center w-full min-w-0">
                    <p className="text-xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                    <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                      <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                      <span>Sessions</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                  <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                    <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                    <span>Sessions</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Row 4: CPC, CPS */}
          <div className="grid grid-cols-2 gap-3">
            {/* CPC Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPC</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{(summaryMetrics.cpc || 0).toFixed(2)}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* CPS Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPS</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{cps.toFixed(2)}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white w-full min-w-0">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 min-w-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Search Campaign Performance</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedSegment === 'monthly'
                  ? 'Monthly breakdown for campaigns'
                  : selectedSegment !== 'none'
                  ? `Daily breakdown for campaigns (${selectedSegment === '7days' ? '7' : selectedSegment === '14days' ? '14' : '30'} days)`
                  : 'Detailed metrics for campaigns and ad groups'
                }
              </p>
            </div>
            
            {/* Search + Segments */}
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              {/* Search Bar */}
              <div className="relative flex-grow md:flex-grow-0">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  placeholder="Search campaigns"
                  className="pl-9 pr-8 py-2.5 w-full md:w-64 bg-white border rounded-xl text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
                {campaignSearch && (
                  <button
                    onClick={() => setCampaignSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Contains / Not contains selector */}
              <div>
                <select
                  value={campaignSearchMode}
                  onChange={(e) => setCampaignSearchMode(e.target.value)}
                  className="px-2.5 py-2.5 bg-white border rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  <option value="contains">contains</option>
                  <option value="not">not contains</option>
                </select>
              </div>

              {/* Segments Dropdown */}
              <div className="relative inline-block">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSegmentDropdownOpen(!isSegmentDropdownOpen);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <LayoutList className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">
                  {selectedSegment === 'none' && 'Segments'}
                  {selectedSegment === '7days' && '7 Days'}
                  {selectedSegment === '14days' && '14 Days'}
                  {selectedSegment === '30days' && '30 Days'}
                  {selectedSegment === 'monthly' && 'Monthly'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* Segments dropdown menu */}
              {isSegmentDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-3 pb-2 mb-2 border-b border-gray-100">
                    <h3 className="text-xs font-medium text-gray-500 uppercase">Date Segments</h3>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedSegment('none');
                      setIsSegmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      selectedSegment === 'none' 
                        ? 'text-blue-600 bg-blue-50/50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    None
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSegment('7days');
                      setIsSegmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      selectedSegment === '7days' 
                        ? 'text-blue-600 bg-blue-50/50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSegment('14days');
                      setIsSegmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      selectedSegment === '14days' 
                        ? 'text-blue-600 bg-blue-50/50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Last 14 Days
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSegment('30days');
                      setIsSegmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      selectedSegment === '30days'
                        ? 'text-blue-600 bg-blue-50/50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSegment('monthly');
                      setIsSegmentDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      selectedSegment === 'monthly'
                        ? 'text-blue-600 bg-blue-50/50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto w-full max-w-full block">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No campaign data available</p>
                <p className="text-sm text-gray-500">Connect your Google Ads account to see campaign performance</p>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No matching campaigns</p>
                <p className="text-sm text-gray-500">Try a different search query or date range</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse min-w-max">
              <thead>
                <tr>
                  <th 
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('text')}
                  >
                    Campaign / Ad Group
                    {sortField === 'text' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('budget')}
                  >
                    Budget
                    {sortField === 'budget' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('spend')}
                  >
                    Cost
                    {sortField === 'spend' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impr.
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ctr')}
                  >
                    CTR
                    {sortField === 'ctr' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpc')}
                  >
                    Avg. CPC
                    {sortField === 'cpc' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conv.
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                    {sortField === 'conversionRate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionValue')}
                  >
                    Revenue
                    {sortField === 'conversionValue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ga4_sessions')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                      <span>Sessions</span>
                    </div>
                    {sortField === 'ga4_sessions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cps')}
                  >
                    CPS
                    {sortField === 'cps' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ga4_purchases')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                      <span>Purchases</span>
                    </div>
                    {sortField === 'ga4_purchases' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ga4_total_revenue')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                      <span>Revenue</span>
                    </div>
                    {sortField === 'ga4_total_revenue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  {hasAdjust && (
                    <>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjust_sessions')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Sessions</span>
                        </div>
                        {sortField === 'adjust_sessions' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjust_installs')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Installs</span>
                        </div>
                        {sortField === 'adjust_installs' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjust_purchase_events')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Purchases</span>
                        </div>
                        {sortField === 'adjust_purchase_events' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjust_all_revenue')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Revenue</span>
                        </div>
                        {sortField === 'adjust_all_revenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50 bg-emerald-50/40"
                        onClick={() => handleSort('total_revenue')}
                        title="GA4 Revenue + Adjust Revenue"
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                          <span className="text-gray-400">+</span>
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Total Rev.</span>
                        </div>
                        {sortField === 'total_revenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    </>
                  )}
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('roas')}
                  >
                    ROAS
                    {sortField === 'roas' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* Campaign Row */}
                    <tr
                      className={`group/row hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaign.id) ? 'bg-blue-50/50 font-semibold' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaign.id, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200 relative">
                        <div className="flex items-center gap-2">
                          {/* Expansion Icon */}
                          <div className="flex items-center justify-center w-5 h-5">
                            {adGroupsLoading.has(campaign.id) ? (
                              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                            ) : expandedCampaigns.has(campaign.id) ? (
                              <ChevronDown className="w-4 h-4 text-blue-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          {/* Status dot + hover arrow + Campaign Name */}
                          {(() => {
                            const live = liveDataMap[String(campaign.id)];
                            const resolvedStatus = cleanStatus(live?.status || campaign.status || '');
                            const liveId = live?.id || campaign.id;
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id); }}
                                  disabled={updatingId === campaign.id}
                                  className="flex-shrink-0 -ml-1 flex items-center gap-0.5 px-1 py-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none group/dot"
                                  title={resolvedStatus === 'ENABLED' ? 'Enabled — click to change' : 'Paused — click to change'}
                                >
                                  {updatingId === campaign.id ? (
                                    <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                  ) : (
                                    <div className={`w-2.5 h-2.5 rounded-full ${resolvedStatus === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'}`} />
                                  )}
                                  <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                </button>
                                {openStatusDropdown === campaign.id && (
                                  <>
                                    <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                    <div className="absolute top-full left-8 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Enable Campaign',
                                            message: `Are you sure you want to enable campaign "${campaign.name}"?`,
                                            variant: 'enable',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'ENABLED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span>Enable</span>
                                        {resolvedStatus === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Pause Campaign',
                                            message: `Are you sure you want to pause campaign "${campaign.name}"?`,
                                            variant: 'pause',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'PAUSED', campaign.id, resolvedStatus, campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span>Pause</span>
                                        {resolvedStatus === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                          <p className="text-[13px] text-gray-900">{campaign.name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right border-r border-gray-200" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const live = liveDataMap[String(campaign.id)];
                          const isEditing = editBudget?.id === campaign.id;
                          const displayBudget = live?.budget != null ? live.budget : campaign.budget;
                          const hasBudgetId = !!(live?.budgetId || campaign.budget_id);

                          if (isEditing) {
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  value={editBudget.value}
                                  onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBudget(campaign.id, campaign.name);
                                    if (e.key === 'Escape') setEditBudget(null);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleSaveBudget(campaign.id, campaign.name)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditBudget(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-sm text-gray-900 font-medium">
                                {displayBudget != null ? `₺${displayBudget.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : isLoadingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 inline" /> : '—'}
                              </span>
                              {hasBudgetId && (
                                <button
                                  onClick={() => setEditBudget({ id: campaign.id, value: displayBudget || 0 })}
                                  disabled={updatingId === campaign.id}
                                  className="opacity-0 group-hover/row:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">
                        ₺{formatNumber(campaign.spend, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.impressions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.ctr) ? '0.00' : campaign.ctr.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.cpc) ? '0.00' : campaign.cpc.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{campaign.conversions || 0}</td>
                      <td className={`text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium ${
                        isNaN(campaign.conversionRate) || campaign.conversionRate === 0 ? 'text-gray-400' :
                        campaign.conversionRate >= 1 ? 'text-emerald-600' : 'text-gray-500'
                      }`}>{isNaN(campaign.conversionRate) ? '0.00' : campaign.conversionRate.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.conversionValue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.ga4_sessions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.cps) ? '0.00' : campaign.cps.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.ga4_purchases || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.ga4_total_revenue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      {hasAdjust && (
                        <>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjust_sessions || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjust_installs || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjust_purchase_events || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.adjust_all_revenue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-semibold text-emerald-700 bg-emerald-50/30">₺{formatNumber((campaign.ga4_total_revenue || 0) + (campaign.adjust_all_revenue || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        </>
                      )}
                      <td className={`text-right py-2.5 px-3 text-[13px] font-semibold ${
                        isNaN(campaign.roas) || campaign.roas === 0 ? 'text-gray-400' :
                        campaign.roas >= 1 ? 'text-emerald-600' : 'text-red-500'
                      }`}>{isNaN(campaign.roas) ? '0.00' : campaign.roas.toFixed(2)}x</td>
                    </tr>

                    {/* Daily Segments Section */}
                    {expandedCampaigns.has(campaign.id) && selectedSegment !== 'none' && (
                      <>
                                                 {(selectedSegment === 'monthly' ? getMonthlyDataForCampaign(campaign.id) : getDailyDataForCampaign(campaign.id)).map((dailyData, dayIndex) => (
                           <tr
                             key={`${campaign.id}_${dailyData.date}`}
                             className={`hover:bg-blue-50/10 ${dailyData.isEmpty ? 'bg-red-50/30 border-l-2 border-red-300' : 'bg-blue-50/5'}`}
                           >
                            <td className="py-2 px-3 border-r border-gray-200">
                              <div className="flex items-center gap-2 pl-[2.75rem]">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <div>
                                    <p className={`text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                      {selectedSegment === 'monthly'
                                        ? new Date(`${dailyData.date}-01`).toLocaleDateString('en-US', {
                                            month: 'long',
                                            year: 'numeric'
                                          })
                                        : new Date(dailyData.date).toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short'
                                          })}
                                    </p>
                                    {dailyData.isEmpty && (
                                      <p className="text-[11px] text-red-400">No data</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${formatNumber(dailyData.spend, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.impressions || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.clicks || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${dailyData.ctr.toFixed(2)}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${dailyData.cpc.toFixed(2)}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.conversions || 0)}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${dailyData.conversionRate.toFixed(2)}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${formatNumber(dailyData.conversionValue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.ga4_sessions || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${(dailyData.cps || 0).toFixed(2)}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.ga4_purchases || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${formatNumber(dailyData.ga4_total_revenue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                            </td>
                            {hasAdjust && (
                              <>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {dailyData.isEmpty ? '—' : (dailyData.adjust_sessions || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {dailyData.isEmpty ? '—' : (dailyData.adjust_installs || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {dailyData.isEmpty ? '—' : (dailyData.adjust_purchase_events || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {dailyData.isEmpty ? '—' : `₺${formatNumber(dailyData.adjust_all_revenue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-semibold bg-emerald-50/30 ${dailyData.isEmpty ? 'text-gray-400' : 'text-emerald-700'}`}>
                                  {dailyData.isEmpty ? '—' : `₺${formatNumber((dailyData.ga4_total_revenue || 0) + (dailyData.adjust_all_revenue || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                                </td>
                              </>
                            )}
                            <td className={`text-right py-2 px-3 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${dailyData.roas.toFixed(2)}x`}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Ad Groups Section — kampanya açıldığında API'den o kampanyaya ait gruplar çekilir */}
                    {expandedCampaigns.has(campaign.id) && selectedSegment === 'none' && (() => {
                      const totalCols = hasAdjust ? 20 : 15;
                      const rows = adGroupsByCampaign[campaign.id];
                      const isLoadingRows = adGroupsLoading.has(campaign.id);
                      const errorMsg = adGroupsError[campaign.id];

                      if (isLoadingRows) {
                        return (
                          <tr className="bg-blue-50/5">
                            <td colSpan={totalCols} className="py-3 px-3">
                              <div className="flex items-center gap-2 pl-[2.75rem] text-[12px] text-gray-500">
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                                Reklam grupları yükleniyor…
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      if (errorMsg) {
                        return (
                          <tr className="bg-red-50/30">
                            <td colSpan={totalCols} className="py-3 px-3">
                              <div className="flex items-center gap-2 pl-[2.75rem] text-[12px] text-red-600">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Reklam grupları alınamadı ({errorMsg})
                                <button
                                  onClick={(e) => { e.stopPropagation(); fetchAdGroupsForCampaign(campaign.id); }}
                                  className="ml-2 underline hover:text-red-700"
                                >
                                  Tekrar dene
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      if (!rows) return null;

                      if (rows.length === 0) {
                        return (
                          <tr className="bg-blue-50/5">
                            <td colSpan={totalCols} className="py-3 px-3">
                              <p className="pl-[2.75rem] text-[12px] text-gray-500">
                                Bu tarih aralığında bu kampanyaya ait reklam grubu verisi yok.
                              </p>
                            </td>
                          </tr>
                        );
                      }

                      return sortAdGroups(rows, sortField, sortDirection).map((adGroup) => (
                        <React.Fragment key={`${campaign.id}_${adGroup.id}`}>
                        <tr className="hover:bg-blue-50/20 bg-blue-50/5">
                          <td className="py-2 px-3 border-r border-gray-200">
                            <div className="flex items-center gap-2 pl-[2.75rem]">
                              <button
                                onClick={(e) => toggleAdGroup(adGroup.id, e, campaign.id)}
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-blue-600 flex-shrink-0"
                                title="Anahtar kelimeleri göster"
                              >
                                {keywordsLoading.has(adGroup.id) ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                                ) : expandedAdGroups.has(adGroup.id) ? (
                                  <ChevronDown className="w-3.5 h-3.5" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${adGroup.status === 'ENABLED' ? 'bg-green-500' : 'bg-gray-400'}`}
                                  title={adGroup.status || 'Bilinmiyor'}
                                />
                                <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.spend || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.ctr || 0).toFixed(2)}%</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{(adGroup.cpc || 0).toFixed(2)}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.conversions || 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.conversionRate || 0).toFixed(2)}%</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.conversionValue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                          <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                          {hasAdjust && (
                            <>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400 bg-emerald-50/30">—</td>
                            </>
                          )}
                          <td className="text-right py-2 px-3 text-[13px] font-medium text-gray-800">{(adGroup.roas || 0).toFixed(2)}x</td>
                        </tr>

                        {/* Anahtar kelimeler — reklam grubu açıldığında anlık çekilir */}
                        {expandedAdGroups.has(adGroup.id) && (() => {
                          const kwRows = keywordsByAdGroup[adGroup.id];
                          const kwLoading = keywordsLoading.has(adGroup.id);
                          const kwError = keywordsError[adGroup.id];

                          if (kwLoading) {
                            return (
                              <tr className="bg-indigo-50/10">
                                <td colSpan={totalCols} className="py-2.5 px-3">
                                  <div className="flex items-center gap-2 pl-[5.25rem] text-[12px] text-gray-500">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                                    Anahtar kelimeler yükleniyor…
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          if (kwError) {
                            return (
                              <tr className="bg-red-50/30">
                                <td colSpan={totalCols} className="py-2.5 px-3">
                                  <div className="flex items-center gap-2 pl-[5.25rem] text-[12px] text-red-600">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    Anahtar kelimeler alınamadı ({kwError})
                                    <button
                                      onClick={(e) => { e.stopPropagation(); fetchKeywordsForAdGroup(campaign.id, adGroup.id); }}
                                      className="ml-2 underline hover:text-red-700"
                                    >
                                      Tekrar dene
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          if (!kwRows) return null;

                          if (kwRows.length === 0) {
                            return (
                              <tr className="bg-indigo-50/10">
                                <td colSpan={totalCols} className="py-2.5 px-3">
                                  <p className="pl-[5.25rem] text-[12px] text-gray-500">
                                    Bu tarih aralığında bu reklam grubuna ait anahtar kelime verisi yok.
                                  </p>
                                </td>
                              </tr>
                            );
                          }

                          const isInfo = impressionShareStatus[adGroup.id];
                          const isNote = isInfo && isInfo.status !== 'ok' ? (
                            <tr key={`${adGroup.id}_isnote`} className="bg-amber-50/40">
                              <td colSpan={totalCols} className="py-1.5 px-3">
                                <p className="pl-[5.25rem] text-[11px] text-amber-700">
                                  {isInfo.status === 'no_connection'
                                    ? 'Impression share çekilemedi: bu hesabın Google Ads bağlantısı bulunamadı.'
                                    : `Impression share çekilemedi (${isInfo.error || 'bilinmeyen hata'}). Diğer metrikler etkilenmedi.`}
                                </p>
                              </td>
                            </tr>
                          ) : null;

                          return [isNote, ...sortKeywords(kwRows, sortField, sortDirection).map((kw) => (
                            <tr key={`${adGroup.id}_${kw.id}`} className="hover:bg-indigo-50/30 bg-indigo-50/10">
                              <td className="py-2 px-3 border-r border-gray-200">
                                <div className="flex items-center gap-2 pl-[5.25rem]">
                                  <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <p className="text-[13px] text-gray-700 truncate max-w-[260px]" title={kw.text}>{kw.text}</p>
                                  {kw.matchType && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                                      {MATCH_TYPE_LABELS[kw.matchType] || kw.matchType}
                                    </span>
                                  )}
                                  {kw.qualityScore != null && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 flex-shrink-0" title="Kalite Puanı">
                                      KP {kw.qualityScore}
                                    </span>
                                  )}
                                  {formatImpressionShare(kw.impressionShare) && (
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                                        kw.impressionShare >= 70 ? 'bg-emerald-50 text-emerald-700'
                                          : kw.impressionShare >= 40 ? 'bg-blue-50 text-blue-700'
                                          : 'bg-orange-50 text-orange-700'
                                      }`}
                                      title={[
                                        `Impression share: ${formatImpressionShare(kw.impressionShare)}`,
                                        formatImpressionShare(kw.topImpressionShare) ? `Top impression share: ${formatImpressionShare(kw.topImpressionShare)}` : null,
                                        formatImpressionShare(kw.absoluteTopImpressionShare) ? `Abs. top impression share: ${formatImpressionShare(kw.absoluteTopImpressionShare)}` : null
                                      ].filter(Boolean).join('\n')}
                                    >
                                      IS {formatImpressionShare(kw.impressionShare)}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">₺{formatNumber(kw.spend || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">{(kw.impressions || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">{(kw.clicks || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">{(kw.ctr || 0).toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">₺{(kw.cpc || 0).toFixed(2)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">{(kw.conversions || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">{(kw.conversionRate || 0).toFixed(2)}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-700">₺{formatNumber(kw.conversionValue || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                              {hasAdjust && (
                                <>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400">—</td>
                                  <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] text-gray-400 bg-emerald-50/30">—</td>
                                </>
                              )}
                              <td className="text-right py-2 px-3 text-[13px] text-gray-700">{(kw.roas || 0).toFixed(2)}x</td>
                            </tr>
                          ))];
                        })()}
                        </React.Fragment>
                      ));
                    })()}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Toplam Satırı - Tüm kampanyaların toplamı */}
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    Total
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-400">
                    —
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(filteredCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {filteredCampaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {filteredCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalImpressions = filteredCampaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0);
                      const totalClicks = filteredCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalClicks = filteredCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalSpend = filteredCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      return totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {filteredCampaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalClicks = filteredCampaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalConversions = filteredCampaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0);
                      return totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(filteredCampaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {filteredCampaigns.reduce((total, campaign) => total + (campaign.ga4_sessions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalSessions = filteredCampaigns.reduce((sum, c) => sum + (c.ga4_sessions || 0), 0);
                      const totalCost = filteredCampaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
                      return totalSessions > 0 ? (totalCost / totalSessions).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {filteredCampaigns.reduce((total, campaign) => total + (campaign.ga4_purchases || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(filteredCampaigns.reduce((total, campaign) => total + (campaign.ga4_total_revenue || 0), 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  {hasAdjust && (
                    <>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {filteredCampaigns.reduce((total, campaign) => total + (campaign.adjust_sessions || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {filteredCampaigns.reduce((total, campaign) => total + (campaign.adjust_installs || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {filteredCampaigns.reduce((total, campaign) => total + (campaign.adjust_purchase_events || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        ₺{formatNumber(filteredCampaigns.reduce((total, campaign) => total + (campaign.adjust_all_revenue || 0), 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-semibold text-[13px] text-emerald-700 bg-emerald-50/30">
                        ₺{formatNumber(filteredCampaigns.reduce((total, campaign) => total + (campaign.ga4_total_revenue || 0) + (campaign.adjust_all_revenue || 0), 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                    </>
                  )}
                  <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalSpend = filteredCampaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      const totalConversionValue = filteredCampaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0);
                      return totalSpend > 0 ? (totalConversionValue / totalSpend).toFixed(2) : '0.00';
                    })()}x
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>

      {/* Ana kampanya tablosu burada bitiyordu */}
      {/* Ek Kartlar: İki kart yan yana */}
      {/* Brand / Generic Breakdown */}
      <Card className="rounded-2xl overflow-hidden bg-white mt-6 min-w-0">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Brand / Generic Breakdown</h2>
              <p className="text-sm text-gray-500 mt-1">Aggregated metrics by campaign name classification</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto w-full max-w-full block">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Category</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Cost</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Sessions</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Clicks</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Conversions</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Revenue</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">
                  <div className="flex items-center justify-end gap-1">
                    <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                    <span>Revenue</span>
                  </div>
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPC</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {brandGenericSummary.rows.map(row => {
                const isOpen = expandedBgCategories.has(row.category);
                const hasCampaigns = row.campaigns.length > 0;
                const toggle = () => {
                  if (!hasCampaigns) return;
                  setExpandedBgCategories(prev => {
                    const next = new Set(prev);
                    if (next.has(row.category)) next.delete(row.category);
                    else next.add(row.category);
                    return next;
                  });
                };
                return (
                  <React.Fragment key={`bg-${row.category}`}>
                    <tr
                      className={`hover:bg-gray-50 ${hasCampaigns ? 'cursor-pointer' : ''}`}
                      onClick={toggle}
                    >
                      <td className="py-2.5 px-3 text-[13px] font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {hasCampaigns ? (
                            isOpen
                              ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                              : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                          ) : (
                            <span className="w-3.5 h-3.5 inline-block" />
                          )}
                          <span>{row.category}</span>
                          {hasCampaigns && (
                            <span className="text-[11px] text-gray-400 font-normal">({row.campaigns.length})</span>
                          )}
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">₺{formatNumber(row.cost, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{formatNumber(row.sessions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{formatNumber(row.clicks, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{formatNumber(row.conversions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">₺{formatNumber(row.adsRevenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">₺{formatNumber(row.ga4Revenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">₺{row.cpc.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">₺{row.cps.toFixed(2)}</td>
                    </tr>
                    {isOpen && row.campaigns.map(c => (
                      <tr key={`bg-${row.category}-${c.id}`} className="bg-gray-50/50 hover:bg-gray-100">
                        <td className="py-2 pl-10 pr-3 text-[12px] text-gray-700">
                          <div className="truncate max-w-[420px]" title={c.name}>{c.name}</div>
                        </td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">₺{formatNumber(c.cost, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">{formatNumber(c.sessions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">{formatNumber(c.clicks, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">{formatNumber(c.conversions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">₺{formatNumber(c.adsRevenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">₺{formatNumber(c.ga4Revenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">₺{c.cpc.toFixed(2)}</td>
                        <td className="text-right py-2 px-3 text-[12px] text-gray-700">₺{c.cps.toFixed(2)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                <td className="py-3 px-3 font-medium text-[13px] text-gray-900">Total</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">₺{formatNumber(brandGenericSummary.totalRow.cost, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">{formatNumber(brandGenericSummary.totalRow.sessions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">{formatNumber(brandGenericSummary.totalRow.clicks, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">{formatNumber(brandGenericSummary.totalRow.conversions, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">₺{formatNumber(brandGenericSummary.totalRow.adsRevenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">₺{formatNumber(brandGenericSummary.totalRow.ga4Revenue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">₺{brandGenericSummary.totalRow.cpc.toFixed(2)}</td>
                <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">₺{brandGenericSummary.totalRow.cps.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 min-w-0">
        {/* Top Keywords Card */}
        <Card className="rounded-2xl overflow-hidden bg-white min-w-0">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Top Performing Keywords</h2>
                <p className="text-sm text-gray-500 mt-1">Keywords with the highest conversion rates and ROI</p>
              </div>
              <div>
                <button className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            {isLoadingKeywords ? (
              <div className="min-h-[250px] flex items-center justify-center">
                <div className="loading-logo-container">
                  <img 
                    src="/logoicon.png" 
                    alt="Glowytics Logo" 
                    className="loading-logo animate-pulse"
                  />
                </div>
              </div>
            ) : processedTopKeywords.length > 0 ? (
              <div className="overflow-y-auto overflow-x-auto max-h-[280px] w-full max-w-full block">
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-left">Keyword</th>
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-right">Cost</th>
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-right">Revenue</th>
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-right">Conversions</th>
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-right">Conv. Rate</th>
                      <th className="py-2 px-3 font-medium text-gray-500 uppercase tracking-wider text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {processedTopKeywords.map((keyword) => (
                      <tr key={keyword.keyword} className="hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-800" title={keyword.keyword}>
                          {keyword.keyword.length > 30 ? `${keyword.keyword.substring(0, 30)}...` : keyword.keyword}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-700">₺{formatNumber(keyword.cost, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        <td className="py-2 px-3 text-right text-gray-700">₺{formatNumber(keyword.conversionValue, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        <td className="py-2 px-3 text-right text-gray-700 font-medium">{keyword.conversions}</td>
                        <td className="text-right text-gray-700">{(keyword.conversionRate).toFixed(2)}%</td>
                        <td className="text-right text-gray-700 font-medium">{keyword.roas.toFixed(2)}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="text-gray-400 mb-2">
                  <Search className="w-12 h-12 mx-auto mb-2" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No keyword performance data found for the selected date range. Please try a different date range or check back later.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Search Term Performance Card */}
        <Card className="rounded-2xl overflow-hidden bg-white min-w-0">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Search Term Performance</h2>
                <p className="text-sm text-gray-500 mt-1">Actual queries users searched for that triggered your ads</p>
              </div>
              <div>
                <button className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            {dataLoading ? (
              <div className="min-h-[250px] flex items-center justify-center">
                <div className="loading-logo-container">
                  <img 
                    src="/logoicon.png" 
                    alt="Glowytics Logo" 
                    className="loading-logo animate-pulse"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="text-gray-400 mb-2">
                  <Search className="w-12 h-12 mx-auto mb-2" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No search term performance data found for the selected date range. Please try a different date range or check back later.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}

      {/* Change History Slide-over */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Filter */}
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();

                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };

                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' :
                              isStatus ? 'bg-amber-100' :
                              isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' :
                                  isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}